<template>
  <v-dialog
    v-model="dialog"
    max-width="750"
    scrollable
    :fullscreen="$vuetify.display.xs"
    style="z-index: 1100 !important;"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span>
          <v-icon class="mr-2">mdi-help-circle</v-icon>
          {{ $t('routes.pod.authenticity.helpTitle') }}
        </span>
        <v-btn icon variant="text" @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4" tabIndex="0">
        <!-- Statuses section -->
        <div class="text-h6 font-weight-medium mb-2">
          {{ $t('routes.pod.authenticity.helpStatusTitle') }}
        </div>
        <div class="d-flex align-center ga-3 mb-2">
          <v-chip color="success" variant="flat" size="small" style="min-width: 110px; justify-content: center;">
            {{ $t('routes.pod.authenticity.status.AUTHENTIC') }}
          </v-chip>
          <span class="text-body-1">{{ $t('routes.pod.authenticity.helpStatusAuthentic') }}</span>
        </div>
        <div class="d-flex align-center ga-3 mb-2">
          <v-chip color="warning" variant="flat" size="small" style="min-width: 110px; justify-content: center;">
            {{ $t('routes.pod.authenticity.status.SUSPICIOUS') }}
          </v-chip>
          <span class="text-body-1">{{ $t('routes.pod.authenticity.helpStatusSuspicious') }}</span>
        </div>
        <div class="d-flex align-center ga-3 mb-2">
          <v-chip color="error" variant="flat" size="small" style="min-width: 110px; justify-content: center;">
            {{ $t('routes.pod.authenticity.status.AI_GENERATED') }}
          </v-chip>
          <span class="text-body-1">{{ $t('routes.pod.authenticity.helpStatusAiGenerated') }}</span>
        </div>
        <div class="d-flex align-center ga-3 mb-2">
          <v-chip color="error" variant="flat" size="small" style="min-width: 110px; justify-content: center;">
            {{ $t('routes.pod.authenticity.status.MANIPULATED') }}
          </v-chip>
          <span class="text-body-1">{{ $t('routes.pod.authenticity.helpStatusManipulated') }}</span>
        </div>
        <div class="d-flex align-center ga-3 mb-2">
          <v-chip color="info" variant="flat" size="small" style="min-width: 110px; justify-content: center;">
            {{ $t('routes.pod.authenticity.status.PENDING') }}
          </v-chip>
          <span class="text-body-1">{{ $t('routes.pod.authenticity.helpStatusPending') }}</span>
        </div>

        <v-divider class="my-4" />

        <!-- Detection Models section -->
        <div>
          <div class="text-h6 font-weight-medium mb-2">
            {{ $t('routes.pod.authenticity.helpModelsTitle') }}
          </div>
          <v-row density="comfortable">
            <v-col cols="12" sm="6">
              <div class="font-weight-medium text-body-1">{{ $t('routes.pod.authenticity.modelGenai') }}</div>
              <div class="text-body-1 text-medium-emphasis mt-1">
                {{ $t('routes.pod.authenticity.modelGenaiDescription') }}
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="font-weight-medium text-body-1">{{ $t('routes.pod.authenticity.modelDeepfake') }}</div>
              <div class="text-body-1 text-medium-emphasis mt-1">
                {{ $t('routes.pod.authenticity.modelDeepfakeDescription') }}
              </div>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-4" />

        <!-- Metadata Analysis section -->
        <div>
          <div class="text-h6 font-weight-medium mb-1">
            {{ $t('routes.pod.authenticity.helpMetadataTitle') }}
          </div>
          <p class="text-body-1 text-medium-emphasis">
            {{ $t('routes.pod.authenticity.helpMetadataDescription') }}
          </p>
          <p class="text-body-1 text-medium-emphasis mt-2 mb-1">
            {{ $t('routes.pod.authenticity.helpMetadataChecksIntro') }}
          </p>
          <v-row density="comfortable">
            <v-col
              v-for="n in 8"
              :key="'meta-check-' + n"
              cols="12"
              sm="6"
            >
              <div class="d-flex align-start ga-2 text-medium-emphasis py-1">
                <v-icon size="18" class="mt-1 flex-shrink-0">mdi-check-circle-outline</v-icon>
                <span class="text-body-1">{{ $t('routes.pod.authenticity.helpMetadataCheck' + n) }}</span>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-4" />

        <!-- Video Metadata Analysis section -->
        <div>
          <div class="text-h6 font-weight-medium mb-1">
            {{ $t('routes.pod.authenticity.helpVideoMetadataTitle') }}
          </div>
          <p class="text-body-1 text-medium-emphasis">
            {{ $t('routes.pod.authenticity.helpVideoMetadataDescription') }}
          </p>
          <p class="text-body-1 text-medium-emphasis mt-2 mb-1">
            {{ $t('routes.pod.authenticity.helpVideoMetadataChecksIntro') }}
          </p>
          <v-row density="comfortable">
            <v-col
              v-for="n in 7"
              :key="'video-check-' + n"
              cols="12"
              sm="6"
            >
              <div class="d-flex align-start ga-2 text-medium-emphasis py-1">
                <v-icon size="18" class="mt-1 flex-shrink-0">mdi-check-circle-outline</v-icon>
                <span class="text-body-1">{{ $t('routes.pod.authenticity.helpVideoMetadataCheck' + n) }}</span>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Forensic details section (conditional) -->
        <template v-if="hasForensicDetails">
          <v-divider class="my-4" />
          <div>
            <div class="text-h6 font-weight-medium mb-1">
              {{ $t('routes.pod.authenticity.helpForensicTitle') }}
            </div>
            <p class="text-body-1 text-medium-emphasis mb-1">
              {{ $t('routes.pod.authenticity.helpForensicDescription') }}
            </p>
            <p class="text-body-1 text-medium-emphasis">
              {{ $t('routes.pod.authenticity.helpForensicSummary') }}
            </p>
          </div>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          @click="dialog = false"
        >
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AuthenticityHelpDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    hasForensicDetails: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },
};
</script>
